import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  createFiling,
  deleteFiling,
  downloadFilingReportCsv,
  downloadFilingReportPdf,
  filingPreview,
  listFilings,
} from '@/api/runs';
import type { FilingOut } from '@/api/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ErrorCard } from '@/components/ui/error-card';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuth } from '../auth/AuthContext';
import { createFilingAffordance, newestFirst } from '@/lib/filingsTab';
import { formatCurrency, formatDate } from '@/lib/format';

/** Filings tab — the run's batched filing sets. Each row is a frozen batch
 *  of approved recovery findings with its own PDF/CSV deliverables; the
 *  Create control commits the current approved delta as the next filing. */
export function FilingsTab({ runId, canReview }: { runId: string; canReview: boolean }) {
  const { userKey, getIdToken } = useAuth();
  const qc = useQueryClient();
  const [label, setLabel] = useState('');

  const filings = useQuery({
    queryKey: ['filings', 'by-run', runId, userKey],
    queryFn: async () => listFilings(runId, (await getIdToken()) ?? ''),
  });
  const preview = useQuery({
    queryKey: ['filings', 'preview', runId, userKey],
    queryFn: async () => filingPreview(runId, (await getIdToken()) ?? ''),
  });

  const create = useMutation({
    mutationFn: async () =>
      createFiling(runId, (await getIdToken()) ?? '', {
        label: label.trim() || null,
      }),
    onSuccess: () => {
      setLabel('');
      void qc.invalidateQueries({ queryKey: ['filings', 'by-run', runId] });
      void qc.invalidateQueries({ queryKey: ['filings', 'preview', runId] });
    },
  });

  if (filings.isPending) {
    return (
      <div className="py-8">
        <Spinner label="Loading filings…" />
      </div>
    );
  }
  if (filings.isError) {
    return <ErrorCard title="Couldn't load filings" error={filings.error} />;
  }

  const rows = newestFirst(filings.data);
  const affordance = createFilingAffordance(preview.data, preview.status);

  return (
    <div className="space-y-4">
      {canReview && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-input bg-muted/20 px-4 py-3">
          <span className="text-sm text-brand-slate">{affordance.summary}</span>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Label (optional)"
            maxLength={256}
            className="h-8 w-56 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          />
          <Button
            type="button"
            size="sm"
            onClick={() => create.mutate()}
            disabled={affordance.disabled || create.isPending}
          >
            {create.isPending ? 'Filing…' : 'Create filing'}
          </Button>
          {create.isError && (
            <span className="text-xs text-destructive">Create failed — see console.</span>
          )}
        </div>
      )}

      {rows.length === 0 ? (
        <p className="py-6 text-sm text-brand-slate">
          No filings yet. Approve findings, then create the first filing to freeze them
          into a batch the customer can file.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Label</TableHead>
              <TableHead className="text-right">Findings</TableHead>
              <TableHead className="text-right">Recovery total</TableHead>
              <TableHead className="text-right">Reports</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((f, i) => (
              // Only the newest filing (rows[0]) can be un-filed — popping
              // the last commit, not rewriting history; the API 409s others.
              <FilingRow
                key={f.id}
                runId={runId}
                filing={f}
                canUnfile={canReview && i === 0}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function FilingRow({
  runId,
  filing,
  canUnfile,
}: {
  runId: string;
  filing: FilingOut;
  canUnfile: boolean;
}) {
  const { getIdToken } = useAuth();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const pdf = useMutation({
    mutationFn: async () => downloadFilingReportPdf(runId, filing.id, (await getIdToken()) ?? ''),
  });
  const csv = useMutation({
    mutationFn: async () => downloadFilingReportCsv(runId, filing.id, (await getIdToken()) ?? ''),
  });
  const unfile = useMutation({
    mutationFn: async () => deleteFiling(runId, filing.id, (await getIdToken()) ?? ''),
    onSuccess: () => {
      setConfirming(false);
      void qc.invalidateQueries({ queryKey: ['filings', 'by-run', runId] });
      void qc.invalidateQueries({ queryKey: ['filings', 'preview', runId] });
    },
  });
  return (
    <TableRow>
      <TableCell className="font-medium">{filing.sequence_number}</TableCell>
      <TableCell>{formatDate(filing.created_at)}</TableCell>
      <TableCell>{filing.label ?? '—'}</TableCell>
      <TableCell className="text-right">{filing.finding_count}</TableCell>
      <TableCell className="text-right">{formatCurrency(filing.recovery_total)}</TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => pdf.mutate()}
            disabled={pdf.isPending}
          >
            {pdf.isPending ? 'Preparing…' : 'PDF'}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => csv.mutate()}
            disabled={csv.isPending}
          >
            {csv.isPending ? 'Preparing…' : 'CSV'}
          </Button>
          {(pdf.isError || csv.isError) && (
            <span className="text-xs text-destructive">Download failed</span>
          )}
          {canUnfile && (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setConfirming(true)}
              >
                Un-file
              </Button>
              <Dialog open={confirming} onClose={() => setConfirming(false)}>
                <DialogHeader>
                  <DialogTitle>Un-file filing #{filing.sequence_number}?</DialogTitle>
                  <DialogDescription>
                    This deletes the filing and releases its {filing.finding_count}{' '}
                    finding{filing.finding_count === 1 ? '' : 's'} back into the
                    unfiled pool — they'll reappear in the next filing preview.
                    Reports already downloaded from it won't match the run anymore.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  {unfile.isError && (
                    <span className="mr-auto text-xs text-destructive">
                      un-file failed — see console
                    </span>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={unfile.isPending}
                    onClick={() => setConfirming(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    disabled={unfile.isPending}
                    onClick={() => unfile.mutate()}
                  >
                    {unfile.isPending ? 'Un-filing…' : 'Un-file'}
                  </Button>
                </DialogFooter>
              </Dialog>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
